import { useCallback, useRef, useState } from 'react'
import * as api from 'api/entityApi'
import { EmptyState, ErrorState, InlineLoader } from 'components/common/Loader'
import { useToast } from 'components/common/Toast'
import { useAsyncData } from 'hooks/useAsyncData'

const AVATAR_FALLBACK =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" fill="#e5f6ec"/><circle cx="20" cy="15" r="7" fill="#bfd4c8"/><path d="M5 39c2-8 8-12 15-12s13 4 15 12z" fill="#bfd4c8"/></svg>`,
  )

/**
 * Port of the `CommentTimeLine` / `CommentTimeLinePhy` / `AddInlineComment`
 * jQuery plugins from jsTaskCommon.js: a comment list plus an inline composer.
 * Attachments upload through `Common/UploadAttachment`, then the comment is
 * saved with `UserCRM/SaveTaskComment`.
 */
export function Comments({
  taskId,
  variant = 'task',
  canAdd = true,
}: {
  taskId: string
  variant?: 'task' | 'physicalapt'
  canAdd?: boolean
}) {
  const toast = useToast()
  const [text, setText] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [posting, setPosting] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const fetcher = useCallback(
    (signal: AbortSignal) => api.fetchComments(taskId, variant, signal),
    [taskId, variant],
  )
  const { data, loading, error, reload } = useAsyncData(fetcher, [taskId, variant])

  const submit = async () => {
    setPosting(true)
    try {
      await api.addComment({ taskId, taskType: variant === 'physicalapt' ? 'physicalapt' : '', remarks: text, files })
      setText('')
      setFiles([])
      if (fileRef.current) fileRef.current.value = ''
      toast.push('success', 'Data submitted successfull')
      reload()
    } catch (err) {
      toast.push('error', (err as Error).message)
    } finally {
      setPosting(false)
    }
  }

  return (
    <div className="comments">
      <h5>Comments</h5>

      {loading ? (
        <InlineLoader />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : !data || data.length === 0 ? (
        <EmptyState message="No comments yet." />
      ) : (
        <ul className="comment-list">
          {data.map((comment, i) => (
            <li key={`${comment.created_on}-${i}`}>
              <img
                className="comment-avatar"
                src={comment.Image || AVATAR_FALLBACK}
                alt={comment.display_name}
                onError={(e) => {
                  e.currentTarget.src = AVATAR_FALLBACK
                }}
              />
              <div className="comment-body">
                <h4>{comment.display_name}</h4>
                <p className="comment-text">{linkify(comment.remarks)}</p>
                {comment.attachments?.length > 0 && (
                  <p className="comment-attachments">
                    📎{' '}
                    {comment.attachments.map((file) => (
                      <a key={file.FileUrl} href={file.FileUrl} target="_blank" rel="noreferrer">
                        {file.FileName}
                      </a>
                    ))}
                  </p>
                )}
                <span className="comment-time">{comment.created_on}</span>
              </div>
            </li>
          ))}
        </ul>
      )}

      {canAdd && (
        <div className="comment-composer">
          <input
            ref={fileRef}
            id={`comment-files-${taskId}`}
            type="file"
            multiple
            onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
          />
          <label htmlFor={`comment-files-${taskId}`} className="btn btn-icon" title="Attach files">
            📎
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="Please Enter Comment"
            value={text}
            disabled={posting}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && text.trim()) {
                e.preventDefault()
                void submit()
              }
            }}
          />
          <button
            type="button"
            className="btn pri-btn"
            disabled={posting || !text.trim()}
            onClick={() => void submit()}
          >
            {posting ? 'Sending…' : 'Send'}
          </button>
          {files.length > 0 && <span className="font12 text-muted">{files.length} file(s)</span>}
        </div>
      )}
    </div>
  )
}

/** Port of `jsFlowCommon.replaceUrlsWithLinks`, without the innerHTML injection. */
function linkify(text: string) {
  if (!text) return null
  const parts = text.split(/(https?:\/\/[^\s]+)/g)
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noreferrer">
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    ),
  )
}
